// pages/PainelDetalheCompras.jsx
import React from 'react';
import dayjs from 'dayjs';
import { Box, Typography, Chip, Divider } from '@mui/material';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import DonutLargeIcon from '@mui/icons-material/DonutLarge';
import BuildIcon from '@mui/icons-material/Build';
import ApartmentIcon from '@mui/icons-material/Apartment';
import TouchAppIcon from '@mui/icons-material/TouchApp';

const fmtCurrency = (v) =>
  (isFinite(v) ? v : 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 2,
  });

const fmtNumber = (v, digits = 2) =>
  (isFinite(v) ? v : 0).toLocaleString('pt-BR', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });

const colorDiff = (v) => (v < 0 ? '#2ecc71' : v > 0 ? '#FF0000' : '#bdc3c7');

function parseGroupParts(grAnalise) {
  // "002 - PNEUS" -> { code: "002", label: "PNEUS" }
  if (!grAnalise) return { code: '', label: '' };
  const [code, rest] = String(grAnalise).split(' - ');
  return { code: (code || '').trim(), label: (rest || '').trim() };
}

function getGroupIcon(grAnalise) {
  const { code, label } = parseGroupParts(grAnalise);
  if (code === '001' || /COMBUSTI/.test(label)) return <LocalGasStationIcon sx={{ mr: 1 }} />;
  if (code === '002' || /PNEU/i.test(label)) return <DonutLargeIcon sx={{ mr: 1 }} />;
  if (code === '003' || /PEÇ|PECA|PECAS/i.test(label)) return <BuildIcon sx={{ mr: 1 }} />;
  if (code === '004' || /ADMIN|PREDIAL/i.test(label)) return <ApartmentIcon sx={{ mr: 1 }} />;
  return null;
}

/**
 * Painel de detalhamento por GR_COMPRA
 * Props:
 *  - rows: Array com linhas agregadas do Setor 2 (VW_BSP_COMPRACONSUMO + META/IMMU da BSP_PREVISAOCOMPRA)
 *  - selectedGroup: string do GR_ANALISE selecionado (ex.: "002 - PNEUS")
 *  - kmAcopTotal: número com a soma de "KM ACOP Realizada" no período (para calcular custo empresa)
 *  - onClear?: função opcional para limpar seleção
 */
export default function PainelDetalheCompras({
  rows = [],
  selectedGroup,
  kmAcopTotal = 0,
  kmAcopPorMes = {},
  onClear,
  selectedCompra,
  onSelectCompra,
}) {


  // Estado vazio: nada selecionado
  if (!selectedGroup) {
    return (
      <Box
        key="pd-empty"
        sx={{

          mt: 2,
          p: 4,
          border: '2px solid #0ea5e9',
          borderRadius: 4,
          bgcolor: 'rgba(14,165,233,0.05)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 220,
        }}
      >
        <Box sx={{ textAlign: 'center', opacity: 0.85 }}>
          <TouchAppIcon sx={{ fontSize: 36, mb: 1 }} />
          <Typography variant="subtitle1">
            Selecione um <b>GRUPO</b> na tabela acima para ver o detalhamento por <b>Itens</b>.
          </Typography>
        </Box>
      </Box>
    );
  }

  // Filtra somente o GR_ANALISE escolhido e agrega por GR_COMPRA
  const filtered = rows.filter(
    (r) => String(r.GR_ANALISE).trim() === String(selectedGroup).trim()
  );

  const byCompra = new Map();
  for (const r of filtered) {
    const chave = r.GR_COMPRA || '—';
    const real = Number(r.GASTO_REAL || 0);
    const meta = Number(r.META || 0);
    const immu = Number(r.IMMU || 0);

    if (!byCompra.has(chave)) byCompra.set(chave, { compra: chave, real: 0, meta: 0, immuPorMes: {} });
    const acc = byCompra.get(chave);
    acc.real += real;
    acc.meta += meta;
    const d = dayjs(r.DATA);
    if (d.isValid()) {
      const mes = d.month() + 1; // 1..12
      acc.immuPorMes[mes] = (acc.immuPorMes[mes] || 0) + immu;
    }

  }

  const itens = [...byCompra.values()]
    .map((it) => {
      const custoEmpresa = kmAcopTotal ? it.real / kmAcopTotal : 0;

      // ===== IMMU com a mesma metodologia do card =====
      const numerador = Object.entries(it.immuPorMes).reduce((sum, [mesStr, immuMes]) => {
        const mes = Number(mesStr);
        const kmMes = Number(kmAcopPorMes?.[mes] ?? 0);
        return sum + (Number(immuMes) * kmMes);
      }, 0);

      const custoRemuImmu = numerador; // R$ (IMMU remunerado)
      const immuPonderado = kmAcopTotal ? (numerador / kmAcopTotal) : 0; // R$/km

      // DIFERENÇA correta: REALIZADO - IMMU (R$)
      const dif = it.real - custoRemuImmu;

      // % em relação ao IMMU (R$)
      const difPct = custoRemuImmu ? (dif / custoRemuImmu) : 0;

      return { ...it, dif, difPct, custoEmpresa, custoRemuImmu, immuPonderado };
    })

    const { code, label } = parseGroupParts(selectedGroup);

  return (
    <Box key={`pd-${selectedGroup}`} sx={{ mt: 3, p: 2, border: '2px solid #0ea5e9', borderRadius: 4 }}>

      {/* Título dinâmico com ícone e botão de limpar (opcional) */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          mb: 1,
        }}
      >
        {getGroupIcon(selectedGroup)}
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {code ? `${code} - ${label}` : selectedGroup}
        </Typography>
        {onClear && (
          <Chip
            size="small"
            label="Limpar"
            onClick={onClear}
            sx={{ height: 22, bgcolor: 'rgba(255,255,255,0.08)' }}
          />
        )}
      </Box>

      {/* Cabeçalho visual (não é <table>) */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr',
          justifyItems: 'center',
          textAlign: 'center',
          gap: 1,
          px: 1,
          py: 1,
          opacity: 0.8,
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 700 }}>
          INSUMOS
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 700 }}>
          IMMU
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 700 }}>
          PROGRAMADO
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 700 }}>
          REALIZADO
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 700 }}>
          DIFERENÇA
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 700 }}>
          DIFERENÇA %
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 700 }}>
          CUSTO IMMU
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 700 }}>
          CUSTO EMPRESA
        </Typography>
      </Box>
      <Divider sx={{ mb: 1, borderColor: 'rgba(255,255,255,0.08)' }} />

      {/* Linhas como “cards” horizontais */}
      {itens.length === 0 ? (
        <Box sx={{ py: 6, textAlign: 'center', opacity: 0.6 }}>
          <Typography variant="body2">
            Não há itens em <b>GR_COMPRA</b> para este grupo no período.
          </Typography>
        </Box>
      ) : (
        itens.map((it) => {
          const isSelected =
            String(selectedCompra || '').trim() === String(it.compra || '').trim();

          return (
            <Box
              key={`${it.compra}-${it.meta}-${it.real}`}
              onClick={() =>
                onSelectCompra && onSelectCompra(isSelected ? null : it.compra)
              }
              sx={{
                cursor: 'pointer',
                display: 'grid',
                gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr',
                justifyItems: 'center',
                textAlign: 'center',
                gap: 1,
                alignItems: 'center',
                px: 1,
                py: 1,
                borderRadius: 2,
                mb: 0.5,
                bgcolor: isSelected
                  ? 'rgba(14,165,233,0.18)'
                  : 'rgba(255,255,255,0.03)',
                '&:hover': {
                  bgcolor: 'rgba(14,165,233,0.24)',
                },
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, justifySelf: 'start', textAlign: 'left' }}
              >
                {it.compra}
              </Typography>
              <Typography variant="body2">
                {fmtCurrency(it.custoRemuImmu)}
              </Typography>
              <Typography variant="body2">{fmtCurrency(it.meta)}</Typography>
              <Typography variant="body2">{fmtCurrency(it.real)}</Typography>
              <Typography variant="body2" sx={{ color: colorDiff(it.dif) }}>
                {fmtCurrency(it.dif)}
              </Typography>
              <Typography variant="body2" sx={{ color: colorDiff(it.dif) }}>
                {fmtNumber(it.difPct * 100)}%
              </Typography>
              <Typography variant="body2">{fmtNumber(it.immuPonderado, 2)}</Typography>
              <Typography variant="body2">{fmtNumber(it.custoEmpresa, 2)}</Typography>
            </Box>
          );
        })
      )}
    </Box>
  );
}
